/**
 * Preparación de la imagen de firma del médico antes de subirla.
 *
 * El backend acepta PNG/JPEG de máximo 300 KB (ver services/signatureStorage.js).
 * Aquí se reduce la imagen en el navegador para que una foto o un escaneo de
 * varios MB llegue como un PNG de decenas de KB.
 */

export const MAX_WIDTH  = 800;
export const MAX_HEIGHT = 300;
export const MAX_UPLOAD_BYTES = 300 * 1024;
export const ACCEPTED_TYPES = ['image/png', 'image/jpeg'];

/**
 * Lee un File de imagen y devuelve un data URL PNG reducido a MAX_WIDTH×MAX_HEIGHT
 * (manteniendo la proporción; nunca amplía una imagen pequeña).
 *
 * @param {File} file
 * @returns {Promise<{ dataUrl: string, width: number, height: number, bytes: number }>}
 */
export function prepareSignatureImage(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error('No se seleccionó ningún archivo'));
      return;
    }
    if (!ACCEPTED_TYPES.includes(file.type)) {
      reject(new Error('La firma debe ser una imagen PNG o JPEG'));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      try {
        const ratio = Math.min(MAX_WIDTH / img.width, MAX_HEIGHT / img.height, 1);
        const width  = Math.max(1, Math.round(img.width * ratio));
        const height = Math.max(1, Math.round(img.height * ratio));

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        // Sin fondo: un PNG con transparencia se ve mejor sobre la línea de firma
        // del PDF. Los JPEG ya vienen con su propio fondo blanco.
        ctx.drawImage(img, 0, 0, width, height);

        const dataUrl = canvas.toDataURL('image/png');
        // El data URL lleva 4 caracteres base64 por cada 3 bytes.
        const base64 = dataUrl.split(',')[1] || '';
        const bytes = Math.round((base64.length * 3) / 4);

        if (bytes > MAX_UPLOAD_BYTES) {
          reject(new Error(
            `La firma pesa ${Math.round(bytes / 1024)} KB después de reducirla y el máximo es ` +
            `${Math.round(MAX_UPLOAD_BYTES / 1024)} KB. Recorta la imagen para dejar solo el trazo.`
          ));
          return;
        }

        resolve({ dataUrl, width, height, bytes });
      } catch (err) {
        reject(new Error('No se pudo procesar la imagen: ' + err.message));
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('El archivo no es una imagen válida'));
    };

    img.src = objectUrl;
  });
}
